import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const EvidenceSection = ({ evidence }) => {
  const [activeTab, setActiveTab] = useState('sources');
  const [expandedEvidence, setExpandedEvidence] = useState(null);

  const evidenceTabs = [
    { id: 'sources', label: 'Sources', icon: 'Link', count: evidence?.sources?.length || 0 },
    { id: 'forensics', label: 'Forensics', icon: 'Search', count: evidence?.forensics?.length || 0 },
    { id: 'attachments', label: 'Attachments', icon: 'Paperclip', count: evidence?.attachments?.length || 0 },
    { id: 'c2pa', label: 'C2PA Verification', icon: 'Shield', count: evidence?.c2pa?.length || 0 }
  ];

  const getSourceTypeIcon = (type) => {
    switch (type) {
      case 'news': return 'Newspaper';
      case 'academic': return 'GraduationCap';
      case 'government': return 'Building';
      case 'social': return 'MessageCircle';
      case 'video': return 'Video';
      case 'image': return 'Image';
      default: return 'Link';
    }
  };

  const getReliabilityColor = (score) => {
    if (score >= 80) return 'text-success bg-success/10';
    if (score >= 60) return 'text-warning bg-warning/10';
    return 'text-destructive bg-destructive/10';
  };

  const renderSources = () => (
    <div className="space-y-4">
      {evidence?.sources?.map((source, index) => (
        <div key={index} className="border border-border rounded-lg p-4 hover:shadow-soft transition-smooth">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                <Icon name={getSourceTypeIcon(source?.type)} size={18} className="text-muted-foreground" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">{source?.title}</h4>
                <p className="text-sm text-muted-foreground">{source?.publisher}</p>
              </div>
            </div>
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${getReliabilityColor(source?.reliability)}`}>
              {source?.reliability}% reliable
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{source?.excerpt}</p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-xs text-muted-foreground">
              <span>{source?.date}</span>
              <span>{source?.language}</span>
              <span>{source?.country}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" iconName="ExternalLink">
                View Source
              </Button>
              <Button variant="ghost" size="icon" iconName="Copy" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderForensics = () => (
    <div className="space-y-4">
      {evidence?.forensics?.map((forensic, index) => (
        <div key={index} className="border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Search" size={18} className="text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">{forensic?.type}</h4>
                <p className="text-sm text-muted-foreground">{forensic?.method}</p>
              </div>
            </div>
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${
              forensic?.result === 'authentic' ? 'text-success bg-success/10' :
              forensic?.result === 'manipulated'? 'text-destructive bg-destructive/10' : 'text-warning bg-warning/10'
            }`}>
              {forensic?.result}
            </div>
          </div>
          
          <div className="mb-3">
            <p className="text-sm text-foreground mb-2">{forensic?.description}</p>
            {forensic?.confidence && (
              <div className="flex items-center space-x-2">
                <span className="text-xs text-muted-foreground">Confidence:</span>
                <div className="flex-1 max-w-32 h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${forensic?.confidence}%` }}
                  />
                </div>
                <span className="text-xs font-medium">{forensic?.confidence}%</span>
              </div>
            )}
          </div>
          
          {forensic?.preview && (
            <div className="mt-3 p-3 bg-muted/50 rounded-lg">
              <Image 
                src={forensic?.preview} 
                alt="Forensic analysis preview"
                className="w-full h-32 object-cover rounded"
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderAttachments = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {evidence?.attachments?.map((attachment, index) => (
        <div key={index} className="border border-border rounded-lg p-4 hover:shadow-soft transition-smooth">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name={attachment?.type === 'image' ? 'Image' : attachment?.type === 'video' ? 'Video' : 'File'} 
                    size={18} className="text-accent" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-foreground truncate">{attachment?.name}</h4>
              <p className="text-sm text-muted-foreground">{attachment?.size} • {attachment?.format}</p>
            </div>
          </div>
          
          {attachment?.preview && (
            <div className="mb-3">
              <Image 
                src={attachment?.preview} 
                alt={attachment?.name}
                className="w-full h-24 object-cover rounded"
              />
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{attachment?.uploadDate}</span>
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" iconName="Download">
                Download
              </Button>
              <Button variant="ghost" size="icon" iconName="Eye" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderC2PA = () => (
    <div className="space-y-4">
      {evidence?.c2pa?.map((credential, index) => (
        <div key={index} className="border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={18} className="text-success" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">C2PA Manifest</h4>
                <p className="text-sm text-muted-foreground">{credential?.issuer}</p>
              </div>
            </div>
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${
              credential?.status === 'verified' ? 'text-success bg-success/10' :
              credential?.status === 'invalid'? 'text-destructive bg-destructive/10' : 'text-warning bg-warning/10'
            }`}>
              {credential?.status}
            </div>
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Created:</span>
              <span className="text-foreground">{credential?.created}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Hash:</span>
              <span className="text-foreground font-mono text-xs">{credential?.hash}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Signature:</span>
              <span className="text-foreground">{credential?.signature}</span>
            </div>
          </div>
          
          <div className="mt-3 pt-3 border-t border-border">
            <Button variant="outline" size="sm" iconName="ExternalLink">
              View Full Manifest
            </Button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'sources': return renderSources();
      case 'forensics': return renderForensics();
      case 'attachments': return renderAttachments();
      case 'c2pa': return renderC2PA();
      default: return renderSources();
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <nav className="flex space-x-1 p-1">
          {evidenceTabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`
                flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth
                ${activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground shadow-soft'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }
              `}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
              {tab?.count > 0 && (
                <span className={`
                  px-1.5 py-0.5 rounded-full text-xs font-medium
                  ${activeTab === tab?.id
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                  }
                `}>
                  {tab?.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>
      {/* Tab Content */}
      <div className="p-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default EvidenceSection;